import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import {
  CubeMeasureEnum,
  selectColorsDistributionMergeFactor,
} from '../../../services/data/impl/cube/cubeSlice';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import { DuckQueryReturnListType } from '../../duckDB/hooks/useDuckQueryScanData';
import { selectCurrMapColorBucket } from '../../map/mapSlice';
import {
  selectCurrMeasure,
  selectCurrPointId,
  selectHoverPointId,
} from '../../select/selectSlice';
import {
  ScanDataEnum,
  convertMeasureCubeRanchToScanDataBlock,
} from '../scanDataDefinitions';
import {
  ValidPointsDropdownOptions,
  selectShowValidPointsDropdown,
} from '../scanDataSlice';
import { OperatorEnum, useScanDataQuery } from './useScanDataQuery';

export enum MapPointsFilterEnum {
  class_name = ScanDataEnum.class_name,
  model_name = ScanDataEnum.model_name,
  model_version = ScanDataEnum.model_version,
  point_id = ScanDataEnum.point_id,
}

export interface FilterScanDataOptions {
  activeFilters?: MapPointsFilterEnum[];
  isMapHoverData?: boolean;
}

export interface MapPointsQueryResult {
  [ScanDataEnum.point_id]: string;
  [ScanDataEnum.rowside_id]: string;
  [ScanDataEnum.rowside_num]: number;
  [ScanDataEnum.view_direction]: string;
  [ScanDataEnum.count_per_meter]: number;
  [ScanDataEnum.detection_length]: number;
  [ScanDataEnum.detection_area]: number;
  [ScanDataEnum.point_location]: string;
  h?: number;
  s?: number;
  v?: number;
  interval_value: number;
}

/**
 * Custom React hook to filter ScanData based on the provided options
 *
 * @param options - FilterScanDataOptions object with tableName and an optional list of activeFilters
 * @returns - Filtered ScanData or null
 */
export const useMapPointsScanData = ({
  activeFilters,
  isMapHoverData = false,
}: FilterScanDataOptions): DuckQueryReturnListType<MapPointsQueryResult> => {
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currScanDataMeasure =
    convertMeasureCubeRanchToScanDataBlock(currMeasure);
  const colorsMergeFactor = useAppSelector(selectColorsDistributionMergeFactor);
  const currPointId = useAppSelector(selectCurrPointId);
  const currHoverPointId = useAppSelector(selectHoverPointId);
  const showValidPointsDropdown = useAppSelector(selectShowValidPointsDropdown);
  const currMapColorBucket = useAppSelector(selectCurrMapColorBucket);
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();

  const pointId = isMapHoverData ? currHoverPointId : currPointId;

  // Determine if the color hsv measure is available and if the current measure is color
  const isColorQueryAndColorsAvailable = useMemo(() => {
    // Check if the color hsv measure is available
    const colorsExistInCube =
      servicesAndMeasuresWithFeatureFilter.availableMeasures.includes(
        CubeMeasureEnum.DetectionFactHasColorHSV
      );

    return (
      colorsExistInCube &&
      currMeasure === CubeMeasureEnum.DetectionFactHasColorHSV &&
      colorsMergeFactor
    );
  }, [
    servicesAndMeasuresWithFeatureFilter.availableMeasures,
    currMeasure,
    colorsMergeFactor,
  ]);

  const [cols, filters, group] = useMemo(() => {
    const cols: string[] = [
      ScanDataEnum.count_per_meter,
      ScanDataEnum.detection_length,
      ScanDataEnum.detection_area,
      ScanDataEnum.point_location,
      ScanDataEnum.point_id,
      ScanDataEnum.rowside_id,
      ScanDataEnum.rowside_num,
      ScanDataEnum.view_direction,
    ];

    if (isColorQueryAndColorsAvailable) {
      cols.push(
        `FLOOR(FLOOR((${ScanDataEnum.color_hsv}::float[])[1] * 360) / ${colorsMergeFactor}) * ${colorsMergeFactor} as h`,
        `AVG((${ScanDataEnum.color_hsv}::float[])[2]) * 100 as s`,
        `AVG((${ScanDataEnum.color_hsv}::float[])[3]) * 100 as v`
      );
    }

    const filters = [];

    if (activeFilters?.includes(MapPointsFilterEnum.point_id)) {
      filters.push({
        col: ScanDataEnum.point_id,
        op: OperatorEnum.eq,
        value: pointId,
      });
    }

    if (currMapColorBucket) {
      filters.push({
        col: 'h',
        op: OperatorEnum.gte,
        value: currMapColorBucket[0],
      });
      filters.push({
        col: 'h',
        op: OperatorEnum.lte,
        value: currMapColorBucket[1],
      });
    }

    if (
      currScanDataMeasure &&
      showValidPointsDropdown === ValidPointsDropdownOptions.showValidPoints
    ) {
      // Typescript cannot resolve currScanDataMeasure as a string so let's help him.
      filters.push({
        col: currScanDataMeasure.toString(),
        op: OperatorEnum.neq,
        value: null,
      });
    }

    if (
      currScanDataMeasure &&
      showValidPointsDropdown === ValidPointsDropdownOptions.showInvalidPoints
    ) {
      filters.push({
        col: currScanDataMeasure.toString(),
        op: OperatorEnum.eq,
        value: null,
      });
    }

    const group: string[] = [
      ScanDataEnum.point_id,
      ScanDataEnum.point_location,
      ScanDataEnum.rowside_id,
      ScanDataEnum.rowside_num,
      ScanDataEnum.view_direction,
      ScanDataEnum.count_per_meter,
      ScanDataEnum.detection_length,
      ScanDataEnum.detection_area,
    ];

    if (isColorQueryAndColorsAvailable) {
      group.unshift('h');
    }

    return [cols, filters, group];
  }, [
    isColorQueryAndColorsAvailable,
    currScanDataMeasure,
    showValidPointsDropdown,
    currMapColorBucket,
  ]);

  // Get the scan data from duckDB filtered by the current feature
  const mapPoints = useScanDataQuery<MapPointsQueryResult>({
    cols,
    filters,
    group,
  });

  return mapPoints;
};
